/**
 * FlowBuilder.jsx
 *
 * WhatsApp drag-and-drop flow builder
 * React 18 + React Flow 11 + MUI v5
 */

import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { LoadingButton } from "@mui/lab";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import RedoRoundedIcon from "@mui/icons-material/RedoRounded";
import CenterFocusStrongRoundedIcon from "@mui/icons-material/CenterFocusStrongRounded";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";

import { nanoid } from "nanoid";

import ReactFlow, {
  Background,
  Controls,
  Handle,
  MiniMap,
  MarkerType,
  ReactFlowProvider,
  SelectionMode,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useReactFlow,
} from "reactflow";

import { useParams } from "react-router-dom";
import axios from "axios";
import { toast as notify } from "react-toastify";

import NodeContainer from "./NodeContainer";
import PaletteCard from "../flows/PalleteCard";
import MessageSelector from "../flows/MessageSelector";
import ReplyButton from "../flows/ReplyButtons";
import MessageBuilder from "../flows/MessageBuilder";
import MessageViewerEdit, { TypeComponentMap } from "./MessageViewerEdit";
import CustomEdge from "./CustomEdge";

import {
  capitalizeFirstLetter,
  getHeader,
  toastError,
} from "../../../utils/util";

import useApiEp from "../../../hooks/useApiEp";
import LoadingWidget from "../../../widgets/LoadingWidget";
import BackBtn from "../../../widgets/BackBtn";
import MediaSelectionModal from "../../../widgets/MediaSelectionModal";

import { useDialog } from "../../../context/DialogContext";
import { API_ENDPOINT } from "../../../utils/constants";
import { UserContext } from "../../../context/UserProvider";

const LEFT_PANEL_WIDTH = 272;
const MAX_HISTORY = 40;

/* -------------------------------------------------------------------------- */
/*                               Helper functions                             */
/* -------------------------------------------------------------------------- */

function cloneGraph(nodes, edges) {
  return JSON.parse(
    JSON.stringify({
      nodes,
      edges,
    })
  );
}

/**
 * Ignore purely UI/transient node fields when checking whether
 * the graph has unsaved changes.
 */
function graphSignature(nodes, edges) {
  const cleanNodes = nodes.map((node) => {
    const { selected, dragging, resizing, ...restNode } = node;

    const { edit, __flowWarning, ...restData } = restNode.data || {};

    return {
      ...restNode,
      data: restData,
    };
  });

  const cleanEdges = edges.map((edge) => {
    const { selected, ...rest } = edge;

    return rest;
  });

  return JSON.stringify({
    nodes: cleanNodes,
    edges: cleanEdges,
  });
}

/**
 * Check whether adding source -> target would create a cycle.
 */
function createsCycle(source, target, edgeList) {
  if (!source || !target) {
    return false;
  }

  const graph = edgeList.reduce((acc, edge) => {
    if (!acc[edge.source]) {
      acc[edge.source] = [];
    }

    acc[edge.source].push(edge.target);

    return acc;
  }, {});

  const queue = [...(graph[target] || [])];
  const seen = new Set(queue);

  while (queue.length > 0) {
    const current = queue.shift();

    if (current === source) {
      return true;
    }

    (graph[current] || []).forEach((next) => {
      if (!seen.has(next)) {
        seen.add(next);
        queue.push(next);
      }
    });
  }

  return false;
}

function graphContainsCycle(nodes, edges) {
  const graph = {};

  nodes.forEach((node) => {
    graph[node.id] = [];
  });

  edges.forEach((edge) => {
    if (graph[edge.source]) {
      graph[edge.source].push(edge.target);
    }
  });

  const visiting = new Set();
  const visited = new Set();

  const dfs = (nodeId) => {
    if (visiting.has(nodeId)) {
      return true;
    }

    if (visited.has(nodeId)) {
      return false;
    }

    visiting.add(nodeId);

    for (const child of graph[nodeId] || []) {
      if (dfs(child)) {
        return true;
      }
    }

    visiting.delete(nodeId);
    visited.add(nodeId);

    return false;
  };

  for (const node of nodes) {
    if (dfs(node.id)) {
      return true;
    }
  }

  return false;
}

function getReachableNodes(starterId, edges) {
  const graph = {};

  edges.forEach((edge) => {
    if (!graph[edge.source]) {
      graph[edge.source] = [];
    }

    graph[edge.source].push(edge.target);
  });

  const reachable = new Set([starterId]);
  const queue = [starterId];

  while (queue.length > 0) {
    const current = queue.shift();

    (graph[current] || []).forEach((next) => {
      if (!reachable.has(next)) {
        reachable.add(next);
        queue.push(next);
      }
    });
  }

  return reachable;
}

function validateFlow(nodes, edges) {
  const errors = [];
  const warnings = [];
  const nodeWarnings = {};

  if (!nodes.length) {
    errors.push("The flow has no nodes.");

    return {
      valid: false,
      errors,
      warnings,
      nodeWarnings,
      unreachableIds: [],
    };
  }

  const starters = nodes.filter((node) => node.type === "starter");

  if (starters.length === 0) {
    errors.push("Add a Starter node to the flow.");
  }

  if (starters.length > 1) {
    errors.push("Only one Starter node is allowed.");

    starters.forEach((starter) => {
      nodeWarnings[starter.id] = "Only one Starter node is allowed.";
    });
  }

  const nodeIds = new Set(nodes.map((node) => node.id));

  const invalidEdges = edges.filter(
    (edge) => !nodeIds.has(edge.source) || !nodeIds.has(edge.target)
  );

  if (invalidEdges.length > 0) {
    errors.push(
      `${invalidEdges.length} connection${
        invalidEdges.length === 1 ? "" : "s"
      } reference missing nodes.`
    );
  }

  const selfLoops = edges.filter((edge) => edge.source === edge.target);

  if (selfLoops.length > 0) {
    errors.push("Self-loop connections are not allowed.");
  }

  if (graphContainsCycle(nodes, edges)) {
    errors.push(
      "The flow contains a loop. Conversation flows must remain acyclic."
    );
  }

  let unreachableIds = [];

  if (starters.length === 1) {
    const starter = starters[0];

    if (!String(starter.data?.text || "").trim()) {
      errors.push("The Starter node needs a trigger.");

      nodeWarnings[starter.id] = "Enter at least one starting trigger.";
    }

    if (nodes.length > 1) {
      const starterHasOutput = edges.some((edge) => edge.source === starter.id);

      if (!starterHasOutput) {
        errors.push("The Starter node is not connected to the conversation.");

        nodeWarnings[starter.id] = "Connect the Starter node to a message.";
      }
    }

    const reachable = getReachableNodes(starter.id, edges);

    unreachableIds = nodes
      .filter((node) => !reachable.has(node.id))
      .map((node) => node.id);

    if (unreachableIds.length > 0) {
      errors.push(
        `${unreachableIds.length} node${
          unreachableIds.length === 1 ? " is" : "s are"
        } unreachable from the Starter.`
      );

      unreachableIds.forEach((id) => {
        nodeWarnings[id] = "This node cannot be reached from the Starter.";
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    nodeWarnings,
    unreachableIds,
  };
}

function getConnectionError(connection, edgeList, allowSelfLoop) {
  const { source, target, sourceHandle, targetHandle } = connection || {};

  if (!source || !target) {
    return "Invalid connection.";
  }

  if (!allowSelfLoop && source === target) {
    return "Self-loops are not allowed.";
  }

  const duplicate = edgeList.some(
    (edge) =>
      edge.source === source &&
      edge.target === target &&
      (edge.sourceHandle || null) === (sourceHandle || null) &&
      (edge.targetHandle || null) === (targetHandle || null)
  );

  if (duplicate) {
    return "This connection already exists.";
  }

  if (createsCycle(source, target, edgeList)) {
    return "That connection would create an infinite loop.";
  }

  return null;
}

/* -------------------------------------------------------------------------- */
/*                                Node styles                                 */
/* -------------------------------------------------------------------------- */

const FlowNodeFrame = ({ selected, warning, start, children }) => {
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 2.5,
        transition: "transform 150ms ease, box-shadow 150ms ease",

        ...(selected && {
          transform: "translateY(-1px)",
          boxShadow: 5,
        }),

        ...(warning && {
          outline: "2px solid",
          outlineColor: "warning.main",
          outlineOffset: 2,
        }),
      }}
    >
      {start && (
        <Chip
          icon={<PlayArrowRoundedIcon sx={{ fontSize: "15px !important" }} />}
          label="START"
          size="small"
          color="success"
          sx={{
            position: "absolute",
            top: -14,
            left: 14,
            zIndex: 5,
            height: 25,
            fontWeight: 800,
            fontSize: 10,
            letterSpacing: 0.7,
            boxShadow: 1,
          }}
        />
      )}

      {warning && (
        <Tooltip title={warning} arrow>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              position: "absolute",
              top: -12,
              right: -12,
              bgcolor: "warning.main",
              color: "warning.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 6,
              boxShadow: 2,
            }}
          >
            <ErrorOutlineRoundedIcon sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>
      )}

      {children}
    </Box>
  );
};

const MessageNode = (props) => {
  return (
    <FlowNodeFrame
      selected={props.selected}
      warning={props.data?.__flowWarning}
    >
      <NodeContainer {...props}>
        <Handle type="target" position="left" />

        {(props.data.category === "list" ||
          props.data.category === "chatform" ||
          props.data.category === "reply_buttons") && (
          <Handle type="source" position="right" />
        )}

        <MessageBuilder {...props} />
      </NodeContainer>
    </FlowNodeFrame>
  );
};

const FlowStarter = (props) => {
  return (
    <FlowNodeFrame
      start
      selected={props.selected}
      warning={props.data?.__flowWarning}
    >
      <NodeContainer {...props}>
        <Handle type="source" position="right" />
        <ReplyButton {...props} />
      </NodeContainer>
    </FlowNodeFrame>
  );
};

const nodeTypes = {
  message: MessageNode,
  starter: FlowStarter,
};

const edgeTypes = {
  custom: CustomEdge,
};

/* -------------------------------------------------------------------------- */
/*                                   Canvas                                   */
/* -------------------------------------------------------------------------- */

const Canvas = ({ allowSelfLoop = false }) => {
  const { id } = useParams();

  const { dialog, hideMediaDialog } = useDialog();

  const { user, loading } = useContext(UserContext);

  const {
    data: chatbotData,
    isLoading,
    isError,
    error,
  } = useApiEp(`/message/get-flow-detail/${id}`);

  const { screenToFlowPosition, fitView } = useReactFlow();

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  const [savingDraft, setSavingDraft] = useState(false);

  const [exportingFlow, setExportingFlow] = useState(false);

  const [exportTarget, setExportTarget] = useState("all");

  const [savedSignature, setSavedSignature] = useState("");

  const [validationReport, setValidationReport] = useState(null);

  const [historyVersion, setHistoryVersion] = useState(0);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  const historyRef = useRef([]);
  const futureRef = useRef([]);

  nodesRef.current = nodes;
  edgesRef.current = edges;

  const showSnackbar = useCallback((message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  /* ---------------------------------------------------------------------- */
  /*                             Initial flow load                           */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (!chatbotData) {
      return;
    }

    const loadedNodes = chatbotData?.nodes ?? [];
    const loadedEdges = chatbotData?.edges ?? [];

    setNodes(loadedNodes);
    setEdges(loadedEdges);

    setSavedSignature(graphSignature(loadedNodes, loadedEdges));

    historyRef.current = [];
    futureRef.current = [];
    setHistoryVersion((value) => value + 1);

    setValidationReport(null);
  }, [chatbotData]);

  /* ---------------------------------------------------------------------- */
  /*                               Dirty state                              */
  /* ---------------------------------------------------------------------- */

  const currentSignature = useMemo(
    () => graphSignature(nodes, edges),
    [nodes, edges]
  );

  const isDirty = Boolean(
    savedSignature && currentSignature !== savedSignature
  );

  useEffect(() => {
    if (!isDirty) {
      return;
    }

    const beforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", beforeUnload);

    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, [isDirty]);

  /*
   * Once the graph changes after a manual validation,
   * remove the old validation report.
   */
  useEffect(() => {
    setValidationReport(null);
  }, [currentSignature]);

  /* ---------------------------------------------------------------------- */
  /*                              Undo / Redo                               */
  /* ---------------------------------------------------------------------- */

  const pushHistory = useCallback(() => {
    const snapshot = cloneGraph(nodesRef.current, edgesRef.current);

    historyRef.current = [
      ...historyRef.current.slice(-(MAX_HISTORY - 1)),
      snapshot,
    ];

    futureRef.current = [];

    setHistoryVersion((value) => value + 1);
  }, []);

  const undo = useCallback(() => {
    if (!historyRef.current.length) {
      return;
    }

    const current = cloneGraph(nodesRef.current, edgesRef.current);

    const previous = historyRef.current[historyRef.current.length - 1];

    historyRef.current = historyRef.current.slice(0, -1);

    futureRef.current = [current, ...futureRef.current].slice(0, MAX_HISTORY);

    setNodes(previous.nodes);
    setEdges(previous.edges);

    setHistoryVersion((value) => value + 1);
  }, []);

  const redo = useCallback(() => {
    if (!futureRef.current.length) {
      return;
    }

    const next = futureRef.current[0];

    const current = cloneGraph(nodesRef.current, edgesRef.current);

    futureRef.current = futureRef.current.slice(1);

    historyRef.current = [
      ...historyRef.current.slice(-(MAX_HISTORY - 1)),
      current,
    ];

    setNodes(next.nodes);
    setEdges(next.edges);

    setHistoryVersion((value) => value + 1);
  }, []);

  const canUndo = historyRef.current.length > 0;

  const canRedo = futureRef.current.length > 0;

  /* ---------------------------------------------------------------------- */
  /*                         React Flow state changes                        */
  /* ---------------------------------------------------------------------- */

  const onNodesChange = useCallback(
    (changes) => {
      const hasRemoval = changes.some((change) => change.type === "remove");

      if (hasRemoval) {
        pushHistory();
      }

      setNodes((current) => applyNodeChanges(changes, current));
    },
    [pushHistory]
  );

  const onEdgesChange = useCallback(
    (changes) => {
      const structuralChange = changes.some(
        (change) => change.type === "remove" || change.type === "add"
      );

      if (structuralChange) {
        pushHistory();
      }

      setEdges((current) => applyEdgeChanges(changes, current));
    },
    [pushHistory]
  );

  const handleNodeDragStart = useCallback(() => {
    /*
     * Store one graph state before a drag starts.
     * We intentionally do NOT store every pixel movement.
     */
    pushHistory();
  }, [pushHistory]);

  /* ---------------------------------------------------------------------- */
  /*                              Connections                               */
  /* ---------------------------------------------------------------------- */

  const isValidConnection = useCallback(
    (connection) => {
      return !getConnectionError(connection, edgesRef.current, allowSelfLoop);
    },
    [allowSelfLoop]
  );

  const onConnect = useCallback(
    (params) => {
      const connectionError = getConnectionError(
        params,
        edgesRef.current,
        allowSelfLoop
      );

      if (connectionError) {
        showSnackbar(connectionError, "warning");

        return;
      }

      pushHistory();

      setEdges((current) =>
        addEdge(
          {
            ...params,
            type: "custom",

            /*
             * Constant animation becomes distracting
             * once flows get larger.
             */
            animated: false,

            style: {
              strokeWidth: 2,
              stroke: "#94a3b8",
            },

            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: "#94a3b8",
            },
          },
          current
        )
      );
    },
    [allowSelfLoop, pushHistory, showSnackbar]
  );

  const handleEdgeUpdate = useCallback(
    (oldEdge, newConnection) => {
      /*
       * Remove the edge being edited while validating.
       * Otherwise it can falsely detect itself as a
       * duplicate/cycle.
       */
      const edgesWithoutOld = edgesRef.current.filter(
        (edge) => edge.id !== oldEdge.id
      );

      const connectionError = getConnectionError(
        newConnection,
        edgesWithoutOld,
        allowSelfLoop
      );

      if (connectionError) {
        showSnackbar(connectionError, "warning");

        return;
      }

      pushHistory();

      setEdges((current) =>
        current.map((edge) => {
          if (edge.id !== oldEdge.id) {
            return edge;
          }

          /*
           * IMPORTANT:
           * Preserve id, edge type, animation,
           * styles and marker properties.
           */
          return {
            ...edge,
            ...newConnection,
          };
        })
      );
    },
    [allowSelfLoop, pushHistory, showSnackbar]
  );

  /* ---------------------------------------------------------------------- */
  /*                               Add nodes                                */
  /* ---------------------------------------------------------------------- */

  const getDefaultData = useCallback((type, position) => {
    if (type === "message") {
      return {
        id: nanoid(6),
        type,
        position,

        data: {
          type,
          category: Object.keys(TypeComponentMap)[0],
        },
      };
    }

    return {
      id: nanoid(6),
      type,
      position,

      data: {
        type,
        text: "",
      },
    };
  }, []);

  const addNode = useCallback(
    (type, position) => {
      const starterExists = nodesRef.current.some(
        (node) => node.type === "starter"
      );

      if (type === "starter" && starterExists) {
        showSnackbar("Starter node is already on the canvas.", "warning");

        return;
      }

      pushHistory();

      const newNode = getDefaultData(type, position);

      setNodes((current) => [...current, newNode]);
    },
    [getDefaultData, pushHistory, showSnackbar]
  );

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData("application/reactflow");

      if (!type) {
        return;
      }

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      addNode(type, position);
    },
    [addNode, screenToFlowPosition]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  /* ---------------------------------------------------------------------- */
  /*                              Active node                               */
  /* ---------------------------------------------------------------------- */

  const activeNode = useMemo(
    () => nodes.find((node) => node.data?.edit),
    [nodes]
  );

  const handleCloseDrawer = useCallback(() => {
    setNodes((current) =>
      current.map((node) => ({
        ...node,

        data: {
          ...node.data,
          edit: false,
        },
      }))
    );
  }, []);

  /* ---------------------------------------------------------------------- */
  /*                               Validation                               */
  /* ---------------------------------------------------------------------- */

  const liveValidation = useMemo(
    () => validateFlow(nodes, edges),
    [nodes, edges]
  );

  const displayNodes = useMemo(() => {
    return nodes.map((node) => ({
      ...node,

      data: {
        ...node.data,
        __flowWarning: liveValidation.nodeWarnings[node.id] || null,
      },
    }));
  }, [nodes, liveValidation.nodeWarnings]);

  const validateCurrentFlow = useCallback(() => {
    const report = validateFlow(nodesRef.current, edgesRef.current);

    setValidationReport(report);

    if (report.valid) {
      showSnackbar("Flow looks good. No structural issues found.", "success");
    } else {
      showSnackbar(
        `${report.errors.length} flow issue${
          report.errors.length === 1 ? "" : "s"
        } found.`,
        "warning"
      );
    }

    return report;
  }, [showSnackbar]);

  /* ---------------------------------------------------------------------- */
  /*                               Save draft                               */
  /* ---------------------------------------------------------------------- */

  const saveDraft = useCallback(async () => {
    if (nodes.length < 1 || edges.length < 1) {
      notify.error("Please create a chatbot flow before saving.");

      return;
    }

    try {
      setSavingDraft(true);

      const response = await axios.post(
        `${API_ENDPOINT}/message/save-chatbot-flow/${id}`,
        {
          nodes,
          edges,
        },
        getHeader()
      );

      setSavedSignature(graphSignature(nodes, edges));

      notify.success(response.data.message || "Flow draft saved successfully.");
    } catch (error) {
      toastError(error);
    } finally {
      setSavingDraft(false);
    }
  }, [id, nodes, edges]);

  /* ---------------------------------------------------------------------- */
  /*                                 Export                                 */
  /* ---------------------------------------------------------------------- */

  const exportFlow = useCallback(async () => {
    const report = validateFlow(nodes, edges);

    setValidationReport(report);

    if (!report.valid) {
      showSnackbar(
        `Fix ${report.errors.length} flow issue${
          report.errors.length === 1 ? "" : "s"
        } before exporting.`,
        "error"
      );

      return;
    }

    try {
      setExportingFlow(true);

      const response = await axios.post(
        `${API_ENDPOINT}/message/export-chatbot-flow/${id}`,
        {
          exportTarget,
        },
        getHeader()
      );

      notify.success(response.data.message || "Flow exported successfully.");
    } catch (error) {
      toastError(error);
    } finally {
      setExportingFlow(false);
    }
  }, [id, nodes, edges, exportTarget, showSnackbar]);

  /* ---------------------------------------------------------------------- */
  /*                           Keyboard shortcuts                            */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    const handleKeyDown = (event) => {
      const isMeta = event.ctrlKey || event.metaKey;

      if (!isMeta) {
        return;
      }

      const tagName = event.target?.tagName?.toLowerCase();

      const isTyping =
        tagName === "input" ||
        tagName === "textarea" ||
        event.target?.isContentEditable;

      if (event.key.toLowerCase() === "s") {
        event.preventDefault();

        if (!savingDraft) {
          saveDraft();
        }

        return;
      }

      /*
       * Let normal browser/input undo work while
       * typing inside fields.
       */
      if (isTyping) {
        return;
      }

      if (event.key.toLowerCase() === "z" && !event.shiftKey) {
        event.preventDefault();
        undo();
        return;
      }

      if (
        (event.key.toLowerCase() === "z" && event.shiftKey) ||
        event.key.toLowerCase() === "y"
      ) {
        event.preventDefault();
        redo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [redo, saveDraft, savingDraft, undo]);

  /* ---------------------------------------------------------------------- */
  /*                             MiniMap styling                             */
  /* ---------------------------------------------------------------------- */

  const getNodeColor = useCallback((node) => {
    if (node.data?.__flowWarning) {
      return "#ed6c02";
    }

    if (node.type === "starter") {
      return "#2e7d32";
    }

    return "#1976d2";
  }, []);

  const flowTitle =
    chatbotData?.name ||
    chatbotData?.chatbotName ||
    chatbotData?.flowName ||
    "Chatbot Flow";

  /* ---------------------------------------------------------------------- */
  /*                              Loading state                              */
  /* ---------------------------------------------------------------------- */

  if (isLoading || loading) {
    return <LoadingWidget label="Loading flow..." />;
  }

  /* ---------------------------------------------------------------------- */
  /*                               Error state                               */
  /* ---------------------------------------------------------------------- */

  if (isError) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f7f8fa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 480,
            p: 4,
            borderRadius: 3,
            textAlign: "center",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 2,
              borderRadius: "50%",
              bgcolor: "error.50",
              color: "error.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ErrorOutlineRoundedIcon sx={{ fontSize: 32 }} />
          </Box>

          <Typography variant="h6" fontWeight={800} gutterBottom>
            Couldn't load this flow
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {error?.response?.data?.message ||
              error?.message ||
              "Something went wrong while loading the chatbot flow."}
          </Typography>

          <Stack direction="row" spacing={1} justifyContent="center">
            <BackBtn />

            <Button
              variant="contained"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Fragment>
      {/* ================================================================ */}
      {/*                            LEFT SIDEBAR                           */}
      {/* ================================================================ */}

      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          width: LEFT_PANEL_WIDTH,
          flexShrink: 0,

          "& .MuiDrawer-paper": {
            width: LEFT_PANEL_WIDTH,
            boxSizing: "border-box",
            borderRight: "1px solid",
            borderColor: "divider",
            bgcolor: "#ffffff",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              px: 2.25,
              pt: 2,
              pb: 1.5,
            }}
          >
            <BackBtn />
          </Box>

          <Divider />

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 2.25,
              py: 2,
            }}
          >
            {/* Flow elements */}

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.5 }}
            >
              <AccountTreeOutlinedIcon color="primary" sx={{ fontSize: 21 }} />

              <Typography variant="subtitle1" fontWeight={800}>
                Flow Elements
              </Typography>
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              Drag an element onto the canvas to build the conversation.
            </Typography>

            <Stack spacing={1.2}>
              <PaletteCard label="Starter" type="starter" />

              <PaletteCard label="Message" type="message" />
            </Stack>

            {/* Tips */}

            <Paper
              variant="outlined"
              sx={{
                mt: 2.5,
                p: 1.5,
                borderRadius: 2.5,
                bgcolor: "#fafbfc",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TipsAndUpdatesOutlinedIcon
                  sx={{
                    mt: 0.2,
                    fontSize: 18,
                    color: "warning.main",
                  }}
                />

                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={800}
                    display="block"
                  >
                    Builder tip
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.55 }}
                  >
                    Every conversation should begin from one Starter node. Avoid
                    disconnected message branches.
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Validation report */}

            {validationReport && (
              <Box sx={{ mt: 2.5 }}>
                <Divider sx={{ mb: 2 }} />

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2" fontWeight={800}>
                    Flow Check
                  </Typography>

                  {validationReport.valid ? (
                    <Chip
                      size="small"
                      color="success"
                      icon={<CheckCircleOutlineRoundedIcon />}
                      label="Ready"
                    />
                  ) : (
                    <Chip
                      size="small"
                      color="warning"
                      icon={<ErrorOutlineRoundedIcon />}
                      label={`${validationReport.errors.length} issue${
                        validationReport.errors.length === 1 ? "" : "s"
                      }`}
                    />
                  )}
                </Stack>

                {validationReport.valid ? (
                  <Alert
                    severity="success"
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      fontSize: 12,
                    }}
                  >
                    No structural problems found.
                  </Alert>
                ) : (
                  <Stack spacing={0.75}>
                    {validationReport.errors.slice(0, 4).map((issue, index) => (
                      <Box
                        key={`${issue}-${index}`}
                        sx={{
                          display: "flex",
                          gap: 0.75,
                          alignItems: "flex-start",
                        }}
                      >
                        <ErrorOutlineRoundedIcon
                          sx={{
                            fontSize: 16,
                            mt: 0.15,
                            color: "warning.main",
                            flexShrink: 0,
                          }}
                        />

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.45,
                          }}
                        >
                          {issue}
                        </Typography>
                      </Box>
                    ))}

                    {validationReport.errors.length > 4 && (
                      <Typography variant="caption" color="text.secondary">
                        +{validationReport.errors.length - 4} more issue
                        {validationReport.errors.length - 4 === 1 ? "" : "s"}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Box>
            )}

            {/* Export */}

            {user?.accounts && user.accounts.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <FileUploadOutlinedIcon
                    sx={{
                      fontSize: 19,
                      color: "text.secondary",
                    }}
                  />

                  <Typography variant="subtitle2" fontWeight={800}>
                    Export Chatbot
                  </Typography>
                </Stack>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    lineHeight: 1.5,
                    mb: 1.5,
                  }}
                >
                  Push this flow to one or more subaccounts.
                </Typography>

                <FormControl fullWidth size="small">
                  <InputLabel id="export-account-label">Subaccount</InputLabel>

                  <Select
                    labelId="export-account-label"
                    value={exportTarget}
                    label="Subaccount"
                    onChange={(event) => setExportTarget(event.target.value)}
                  >
                    <MenuItem value="all">
                      All ({user.accounts.length}) Subaccounts
                    </MenuItem>

                    {user.accounts.map((account) => (
                      <MenuItem key={account} value={account}>
                        {account}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <LoadingButton
                  sx={{
                    mt: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                  fullWidth
                  loading={exportingFlow}
                  variant="outlined"
                  startIcon={<FileUploadOutlinedIcon />}
                  onClick={exportFlow}
                >
                  Export Flow
                </LoadingButton>
              </Box>
            )}
          </Box>

          {/* Keyboard hint */}

          <Box
            sx={{
              px: 2.25,
              py: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "#fafafa",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              <strong>⌘/Ctrl + S</strong> Save &nbsp;·&nbsp;
              <strong>⌘/Ctrl + Z</strong> Undo
            </Typography>
          </Box>
        </Box>
      </Drawer>

      {/* ================================================================ */}
      {/*                              CANVAS                               */}
      {/* ================================================================ */}

      <Box
        sx={{
          height: "100vh",
          ml: `${LEFT_PANEL_WIDTH}px`,
          position: "relative",
          bgcolor: "#f7f8fa",
          overflow: "hidden",
        }}
        onDrop={onDrop}
        onDragOver={onDragOver}
      >
        {/* -------------------------------------------------------------- */}
        {/*                         TOP TOOLBAR                            */}
        {/* -------------------------------------------------------------- */}

        <Paper
          elevation={0}
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            right: 16,
            zIndex: 10,

            px: 1.5,
            py: 1.1,

            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",

            boxShadow: "0 8px 30px rgba(15, 23, 42, 0.07)",

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Flow title */}

          <Stack
            direction="row"
            spacing={1.3}
            alignItems="center"
            sx={{
              minWidth: 0,
              flex: 1,
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <MessageOutlinedIcon sx={{ fontSize: 21 }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={800} noWrap>
                {flowTitle}
              </Typography>

              <Stack direction="row" spacing={0.7} alignItems="center">
                {isDirty ? (
                  <>
                    <CloudOffOutlinedIcon
                      sx={{
                        fontSize: 14,
                        color: "text.secondary",
                      }}
                    />

                    <Typography variant="caption" color="text.secondary">
                      Unsaved changes
                    </Typography>
                  </>
                ) : (
                  <>
                    <CloudDoneOutlinedIcon
                      sx={{
                        fontSize: 14,
                        color: "success.main",
                      }}
                    />

                    <Typography variant="caption" color="text.secondary">
                      Saved
                    </Typography>
                  </>
                )}

                <Typography variant="caption" color="text.disabled">
                  •
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {nodes.length} node
                  {nodes.length === 1 ? "" : "s"}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          {/* Toolbar actions */}

          <Stack direction="row" spacing={0.6} alignItems="center">
            <Tooltip title="Undo">
              <span>
                <IconButton size="small" disabled={!canUndo} onClick={undo}>
                  <UndoRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Redo">
              <span>
                <IconButton size="small" disabled={!canRedo} onClick={redo}>
                  <RedoRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Tooltip title="Fit entire flow">
              <IconButton
                size="small"
                onClick={() =>
                  fitView({
                    padding: 0.18,
                  })
                }
              >
                <CenterFocusStrongRoundedIcon />
              </IconButton>
            </Tooltip>

            <Button
              size="small"
              variant="text"
              startIcon={<FactCheckOutlinedIcon />}
              onClick={validateCurrentFlow}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                px: 1.2,
              }}
            >
              Validate
            </Button>

            <LoadingButton
              size="small"
              variant="contained"
              loading={savingDraft}
              startIcon={<SaveOutlinedIcon />}
              onClick={saveDraft}
              sx={{
                ml: 0.5,
                px: 1.8,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 800,
                boxShadow: "none",
              }}
            >
              Save Draft
            </LoadingButton>
          </Stack>
        </Paper>

        {/* -------------------------------------------------------------- */}
        {/*                       EMPTY CANVAS STATE                       */}
        {/* -------------------------------------------------------------- */}

        {nodes.length === 0 && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                width: 370,
                p: 4,
                textAlign: "center",
                borderRadius: 4,
                border: "1px dashed",
                borderColor: "divider",
                bgcolor: "rgba(255,255,255,0.88)",
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  mx: "auto",
                  mb: 2,
                  borderRadius: 3,
                  bgcolor: "primary.50",
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AccountTreeOutlinedIcon sx={{ fontSize: 32 }} />
              </Box>

              <Typography variant="h6" fontWeight={800} gutterBottom>
                Build your conversation
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  lineHeight: 1.7,
                  mb: 2,
                }}
              >
                Drag a Starter node from the left panel onto the canvas, then
                connect messages to create your chatbot.
              </Typography>

              <Chip
                icon={<PlayArrowRoundedIcon />}
                label="Starter → Message → Reply"
                variant="outlined"
                size="small"
              />
            </Paper>
          </Box>
        )}

        {/* -------------------------------------------------------------- */}
        {/*                          REACT FLOW                            */}
        {/* -------------------------------------------------------------- */}

        <ReactFlow
          nodes={displayNodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgeUpdate={handleEdgeUpdate}
          onNodeDragStart={handleNodeDragStart}
          onPaneClick={handleCloseDrawer}
          isValidConnection={isValidConnection}
          selectionMode={SelectionMode.Partial}
          multiSelectionKeyCode={["Meta", "Control"]}
          selectionOnDrag
          panOnDrag={[0, 1]}
          fitView
        >
          <Background gap={22} size={1} color="#d8dde5" />

          <Controls
            style={{
              marginBottom: 18,
              marginLeft: 18,
              borderRadius: 10,
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,0.08)",
              boxShadow: "0 4px 14px rgba(15,23,42,0.08)",
            }}
          />

          <MiniMap
            pannable
            zoomable
            nodeStrokeColor={getNodeColor}
            nodeColor={getNodeColor}
            maskColor="rgba(248,250,252,0.72)"
            style={{
              borderRadius: 12,
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,0.08)",
              boxShadow: "0 5px 18px rgba(15,23,42,0.08)",
            }}
          />
        </ReactFlow>
      </Box>

      {/* ================================================================ */}
      {/*                          NODE EDITOR                              */}
      {/* ================================================================ */}

      <Drawer
        open={Boolean(activeNode)}
        onClose={handleCloseDrawer}
        anchor="right"
        sx={{
          "& .MuiDrawer-paper": {
            width: {
              xs: "100vw",
              sm: 460,
              md: 520,
            },
            bgcolor: "#ffffff",
          },
        }}
      >
        {activeNode && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Editor header */}

            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 5,
                bgcolor: "background.paper",
                px: 2.5,
                py: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor:
                        activeNode.type === "starter"
                          ? "success.50"
                          : "primary.50",

                      color:
                        activeNode.type === "starter"
                          ? "success.main"
                          : "primary.main",

                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {activeNode.type === "starter" ? (
                      <PlayArrowRoundedIcon />
                    ) : (
                      <MessageOutlinedIcon />
                    )}
                  </Box>

                  <Box>
                    <Typography variant="subtitle1" fontWeight={800}>
                      Edit {capitalizeFirstLetter(activeNode.type)}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      Configure this conversation step
                    </Typography>
                  </Box>
                </Stack>

                <Tooltip title="Close">
                  <IconButton
                    onClick={handleCloseDrawer}
                    size="small"
                    sx={{
                      bgcolor: "#f5f6f8",

                      "&:hover": {
                        bgcolor: "#eceff3",
                      },
                    }}
                  >
                    <CloseRoundedIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {/* Editor content */}

            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                py: 2.5,
              }}
            >
              {activeNode.type === "message" && (
                <>
                  <Box
                    sx={{
                      px: 3,
                      mb: 2.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={800}
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mb: 0.75,
                        textTransform: "uppercase",
                        letterSpacing: 0.65,
                      }}
                    >
                      Message Type
                    </Typography>

                    <MessageSelector
                      value={activeNode.data.category}
                      onChange={(newValue) => {
                        pushHistory();

                        setNodes((current) =>
                          current.map((node) =>
                            node.id === activeNode.id
                              ? {
                                  ...node,

                                  data: {
                                    ...node.data,
                                    category: newValue,
                                  },
                                }
                              : node
                          )
                        );
                      }}
                    />
                  </Box>

                  <Divider />

                  <Box sx={{ pt: 1 }}>
                    <MessageViewerEdit
                      id={activeNode.id}
                      data={activeNode.data}
                      handleClose={handleCloseDrawer}
                    />
                  </Box>
                </>
              )}

              {activeNode.type === "starter" && (
                <Box px={3}>
                  <Typography
                    variant="caption"
                    fontWeight={800}
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mb: 0.75,
                      textTransform: "uppercase",
                      letterSpacing: 0.65,
                    }}
                  >
                    Starting Trigger
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1.5,
                      lineHeight: 1.6,
                    }}
                  >
                    Enter the text that should trigger this chatbot flow.
                  </Typography>

                  <TextField
                    fullWidth
                    value={activeNode.data.text || ""}
                    onChange={(event) => {
                      const value = event.target.value;

                      pushHistory();

                      setNodes((current) =>
                        current.map((node) =>
                          node.id === activeNode.id
                            ? {
                                ...node,

                                data: {
                                  ...node.data,
                                  text: value,
                                },
                              }
                            : node
                        )
                      );
                    }}
                    label="Starting trigger"
                    multiline
                    maxRows={5}
                    rows={3}
                    placeholder="e.g. Hi, Hello, Interested"
                    helperText="Example: Hi, Hello, Interested"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  <Alert
                    severity="info"
                    variant="outlined"
                    icon={<InfoOutlinedIcon />}
                    sx={{
                      mt: 2,
                      borderRadius: 2,
                    }}
                  >
                    This is the entry point of the conversation. Only one
                    Starter node is allowed per flow.
                  </Alert>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* ================================================================ */}
      {/*                            FEEDBACK                               */}
      {/* ================================================================ */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() =>
          setSnackbar((current) => ({
            ...current,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((current) => ({
              ...current,
              open: false,
            }))
          }
          sx={{
            minWidth: 320,
            borderRadius: 2,
            boxShadow: 4,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <MediaSelectionModal dialog={dialog} onClose={hideMediaDialog} />
    </Fragment>
  );
};

/* -------------------------------------------------------------------------- */
/*                                  Wrapper                                   */
/* -------------------------------------------------------------------------- */

const ChatbotFlowBuilder = () => {
  return (
    <ReactFlowProvider>
      <Canvas />
    </ReactFlowProvider>
  );
};

export default ChatbotFlowBuilder;
